import type { DocumentNode } from "graphql";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { clockSkewDetection, isClockSkewError, isNetworkError, markClockSkew } from "./clockSkew";

export { isClockSkewError, isForbiddenError, isNetworkError } from "./clockSkew";

/** App `NetworkRetryConfig` と同じ。3 = 初回 + リトライ 2 回。 */
export const NETWORK_RETRY_MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 800;

export function delayForAttempt(attempt: number): number {
  return BASE_DELAY_MS * 2 ** (attempt - 1);
}

export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function isGraphqlMutation(query: string | DocumentNode): boolean {
  if (typeof query === "string") {
    return /^\s*mutation\b/i.test(query);
  }

  return query.definitions.some(
    (definition) => definition.kind === "OperationDefinition" && definition.operation === "mutation"
  );
}

/**
 * 一過性のネットワーク失敗ならリトライする。
 * オフライン・4xx・S3 NoSuchKey・時刻ずれは即失敗。
 */
export function isRetryableNetworkError(error: unknown): boolean {
  if (isOffline()) return false;
  if (isClockSkewError(error)) return false;
  if (!error || typeof error !== "object") return false;

  const e = error as {
    message?: string;
    name?: string;
    status?: number;
    code?: string | number;
    response?: { status?: number };
    errors?: Array<{ message?: string }>;
  };

  if (e.name === "NoSuchKey") return false;

  const status = e.response?.status ?? e.status;
  if (typeof status === "number") {
    if (status >= 400 && status < 500) return false;
    if (status >= 500) return true;
  }

  if (isNetworkError(e)) return true;

  const message = String(e.errors?.[0]?.message ?? e.message ?? e.code ?? "");
  return /ERR_CONNECTION_RESET|ERR_NETWORK_CHANGED|ERR_INTERNET_DISCONNECTED|ECONNRESET|ETIMEDOUT|timeout/i.test(
    message
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** テストで待機を差し替えるため。 */
export const networkRetrySleep = {
  sleep,
};

export async function withNetworkRetry<T>(operation: () => Promise<T>, options?: { retry?: boolean }): Promise<T> {
  const maxAttempts = options?.retry === false ? 1 : NETWORK_RETRY_MAX_ATTEMPTS;
  let lastError: unknown;

  /* リトライは待機を挟んで逐次実行する */
  /* eslint-disable no-await-in-loop */
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (isClockSkewError(error)) {
        throw error;
      }
      if (isNetworkError(error) && (await clockSkewDetection.detect())) {
        throw markClockSkew(error);
      }
      if (attempt >= maxAttempts || !isRetryableNetworkError(error)) {
        throw error;
      }
      await networkRetrySleep.sleep(delayForAttempt(attempt));
    }
  }
  /* eslint-enable no-await-in-loop */

  throw lastError;
}

export function retryableGet<T = any>(
  client: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return withNetworkRetry(() => client.get<T>(url, config));
}
